import DashboardFooter from "./DashboardFooter";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';
import PatternBackground from "../../components/PatternBackground";
import { ReactComponent as LifeIcon } from '../../assets/UiElements/life.svg';
import { ReactComponent as PensionIcon } from '../../assets/UiElements/retail.svg';
import { ReactComponent as DasIcon } from '../../assets/UiElements/das.svg';
import { ReactComponent as AddButtonIcon } from '../../assets/UiElements/Buttons.svg';
import { ReactComponent as CorporateIcon } from '../../assets/UiElements/corporate.svg';
import {
  Phone, Mail, ChevronRight, ChevronLeft, CreditCard,
  TrendingUp, Gift, ClipboardList, BarChart3
} from 'lucide-react';
import LinkAccountModal from "./LinkAccountModal";
import LinkSuccessModal from "./LinkSuccessModal";
import DashboardModal from "../../components/DashboardModal";
import FinancialWellnessModal from "../../components/FinancialWellnessModal";
import { useRelationshipOfficer } from "../../hooks/dashboard/useRelationshipOfficer";
import { useProfile } from "../../hooks/useAuth";
import { useAllCorporateAccounts } from '../../hooks/dashboard/useAllCoprateAccount';
import RecommendedForYou from '../../components/LegacyRecommendedForYou';
import { Box, styled } from '@mui/material';
import { usePensionsSummary } from '../../hooks/dashboard/usePensionSummary';
import { usePolicySummary } from '../../hooks/dashboard/usePolicySummary';
import { useDasFundSummary } from '../../hooks/products/das/useDasFundSummary';
import { notifyUser } from '../../util/util';
import DashboardNews from "../../components/LegacyNewsCard"
// PNG Asset Imports
import UserIcon from '../../assets/img/Gemini_Generated_Image_4ydxck4ydxck4ydx-removebg-preview.png'; 
import DiamondIcon from '../../assets/img/streamline-plump-color_diamond-1-flat.png';

function getBasePolicyCardData(pensionSummaryData, policySummaryData, dasSummaryData) {
  const totalPolicies = policySummaryData?.data?.total_policies || 0;
  const totalLifeInvestment = policySummaryData?.data?.total_life_investment || 0;
  const pensionCount = pensionSummaryData?.data?.total_pensions || 0;

  const formatCurrency = (val) => {
    const num = typeof val === 'string' ? parseFloat(val.replace(/[^0-9.-]/g, '')) : val;
    return num ? `₵${num.toLocaleString()}` : "₵0.00";
  };

  const dasSchemeCount = dasSummaryData?.total_schemes;
  const dasPensionCount = dasSummaryData?.total_pensions;
  // The total pension figure is the actual DAS amount — show it in cedis
  const formattedDasPension =
    dasPensionCount !== undefined
      ? `₵${Number(dasPensionCount).toLocaleString()}`
      : '';

  return [
    {
      label: "Life Insurance",
      accountsCount: `${totalPolicies} accounts`,
      mainText1: formatCurrency(totalLifeInvestment),
      mainText2: "Available balance",
      extraText: "",
      icon: 'life',
    },
    {
      label: "Pension",
      accountsCount: `${pensionCount} accounts`,
      mainText1: formatCurrency(pensionSummaryData?.data?.total_investment),
      mainText2: "Your total contributions",
      icon: 'pension',
    },
    {
      label: "DAS",
      accountsCount:
        dasSchemeCount !== undefined
          ? `${dasSchemeCount} Scheme${dasSchemeCount === 1 ? '' : 's'}`
          : '',
      mainText1: formattedDasPension,
      mainText2: 'Available balance',
      icon: 'das',
    },
  ];
}

function AffluentDashboardPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const { data: policySummaryData } = usePolicySummary();
  const { data: corporateAccountsData } = useAllCorporateAccounts();
  const { data: pensionSummaryData } = usePensionsSummary();
  const { data: dasFundSummaryData } = useDasFundSummary();
  const dasSummaryData = React.useMemo(() => {
    const d = dasFundSummaryData?.data;
    return {
      total_schemes: d?.total_schemes,
      total_pensions: d?.total_pensions,
    };
  }, [dasFundSummaryData]);
  const { data: profileResponse } = useProfile();
  const { data: relationshipOfficer, isLoading: isLoadingOfficer } = useRelationshipOfficer();
  
  const profile = profileResponse?.data || {};
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showFinancialWellness, setShowFinancialWellness] = useState(false);

  const onNavigate = (section) => {
    switch(section) {
      case 'services':
        navigate('/dashboard/complimentary-services');
        break;
      case 'card':
        navigate('/dashboard/affluent-card');
        break;
      case 'insights':
        navigate('/dashboard/financial-insights');
        break;
        case 'track-claims':
          navigate('/dashboard/track-claims');
          break;
      default:
        break;
    }
  };
  
  const patternBgRef = useRef(null);
  const policyScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  let corporateCards = [];
  const corporateList = corporateAccountsData?.data;
  const hideCorporate = process.env.REACT_APP_HIDE_CORPORATE === 'true';

  if (!hideCorporate && process.env.REACT_APP_COMING_SOON_BUTTONS !== 'true') {
    if (Array.isArray(corporateList) && corporateList.length > 0) {
      corporateCards = [{
        label: "Corporate Account",
        hidden: true, // UI hidden, not deleted
        accountsCount: `${corporateList.length} account${corporateList.length > 1 ? 's' : ''}`,
        mainText1: "",
        mainText2: "View and manage your corporate accounts",
        icon: 'corporate',
        isCorporate: true,
        corpRaw: corporateList,
      }];
    } else {
      corporateCards = [{
        label: "Corporate Account",
        hidden: true, // UI hidden, not deleted
        accountsCount: "",
        mainText1: "",
        mainText2: "Link your corporate account",
        icon: 'corporate',
        isCorporate: true,
        isLinkCard: true,
      }];
    }
  }

  // Solutions card - shown after corporate card
  const solutionsCard = [{
    label: "SOLUTIONS FOR YOU",
    accountsCount: "",
    mainText1: "",
    mainText2: "Learn More About Our Solutions",
    icon: 'element',
  }];

  useEffect(() => {
    const container = policyScrollRef.current;
    if (!container) return;
    
    const updateScrollState = () => {
      const el = policyScrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 10);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
    };

    updateScrollState();
    container.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [corporateCards.length, solutionsCard.length]);

  // Show dashboard modal on mount
  useEffect(() => {
    setShowDashboardModal(true);
  }, []);

  const scrollPolicyCards = (direction) => {
    const container = policyScrollRef.current;
    if (!container) return;
    const delta = direction === "left" ? -300 : 300;
    container.scrollBy({ left: delta, behavior: "smooth" });
  };

  const QuickAction = ({ icon, label, onClick }) => {
    const words = label.split(' ');
    const firstWord = words[0];
    const rest = words.slice(1).join(' ');
    return (
      <div onClick={onClick} className="w-[164px] h-[160px] bg-[#2D293D] p-6 rounded-[20px] flex flex-col items-start gap-2 hover:scale-[1.02] transition-transform cursor-pointer">
        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">{icon}</div>
        <div className="text-white text-xs font-semibold leading-tight">
          <div>{firstWord}</div>
          {rest && <div>{rest}</div>}
        </div>
      </div>
    );
  };

   const ScrollButton = styled('button')(({ theme, direction }) => ({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 36,
    height: 36,
    backgroundColor: "rgba(16, 185, 129, 0.15)",
    color: "#10b981",
    border: "none",
    borderRadius: "50%",
    zIndex: 15,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    ...(direction === "left" ? { left: -16 } : { right: -16 }),
    "&:hover": {
      backgroundColor: "rgba(16, 185, 129, 0.25)",
      color: "#34d399",
    },
    "&:disabled": {
      opacity: 0,
      visibility: "hidden",
      cursor: "default",
    },
    [theme.breakpoints.down("sm")]: {
      width: 32,
      height: 32,
      backgroundColor: "transparent",
      color: "#10b981",
      ...(direction === "left" ? { left: -12 } : { right: -12 }),
    },
  }));

  const PolicyCard = ({ data, idx, onClick }) => {
    const isLinkCard = data.isLinkCard;
    const isSolutions = data.label === 'SOLUTIONS FOR YOU';
    const isLifeOrPension = data.label === 'Life Insurance' || data.label === 'Pension' || data.label === 'DAS';

    return (
       <div
        onClick={onClick}
        className="w-[280px] min-h-[320px] bg-[#2D293D] text-white rounded-[24px] shadow-lg p-6 flex flex-col relative cursor-pointer transition-all duration-300 hover:bg-[#36314a] group"
        style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}
      >
        {/* Top Row: Label and Arrow */}
        <div className="w-full flex items-center justify-between mb-3">
          <span className="font-bold tracking-tight text-white text-xl">
            {isSolutions ? "Solutions for you" : data.label}
          </span>
          {!isLinkCard && (
            <ChevronRight size={20} className="text-white opacity-80 group-hover:translate-x-1 transition-transform" />
          )}
        </div>

        {/* Accounts Text and Count - Only show "You Have" for Life and Pension cards */}
        {isLifeOrPension && (
          <div className="mb-3 text-left w-full">
            <div className="text-white font-light text-sm mb-1">
              You Have
            </div>
            <div className="text-white font-light text-base">
              {data.accountsCount}
            </div>
          </div>
        )}

        {/* For Corporate and Solutions cards, just show accountsCount without "You Have" */}
        {!isLifeOrPension && data.accountsCount && !isLinkCard && (
          <div className="mb-3 text-left w-full">
            <div className="text-white font-light text-base">
              {data.accountsCount}
            </div>
          </div>
        )}

        {/* Middle Icon Area - LEFT ALIGNED */}
        <div className={`mb-3 flex justify-start w-full`}>
          <div className="flex items-center">
            {isLinkCard ? (
              <AddButtonIcon style={{ width: 48, height: 48, display: 'block' }} />
            ) : (
              <>
                {data.icon === 'life' && <LifeIcon style={{ width: 48, height: 48, display: 'block', color: '#fff', fill: '#fff' }} />}
                {data.icon === 'pension' && <PensionIcon style={{ width: 48, height: 48, display: 'block', color: '#fff', fill: '#fff' }} />}
                {data.icon === 'das' && <DasIcon style={{ width: 48, height: 48, display: 'block', color: '#fff', fill: '#fff' }} />}
                {data.icon === 'corporate' && <CorporateIcon style={{ width: 48, height: 48, display: 'block' }} />}
                {isSolutions && <div className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center font-serif text-xl">i</div>}
              </>
            )}
          </div>
        </div>

        {/* Main Amount/Text */}
        <div className="text-white font-bold text-2xl text-left w-full mb-1">
          {data.mainText1}
        </div>

        {/* Keep content compact; avoid forcing extra vertical gap */}
        <div className="h-1" />

        {/* Secondary Text */}
        <div className="text-white text-base text-left w-full mb-1">
          {data.mainText2}
        </div>

        {/* Extra Text for Life Insurance card */}
        {data.label === 'Life Insurance' && data.extraText && (
          <div className="text-white text-xs text-left w-full mb-3 font-light">
            {data.extraText}
          </div>
        )}

        {/* Bottom Progress Bar - Fixed at bottom */}
        <div className="w-full h-1 rounded-full bg-gradient-to-r from-[#006400] via-[#00b050] to-[#90ee90] mt-2"></div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#0f1117]">
      <PatternBackground ref={patternBgRef} fullWidthOnMobile>
        <div className="w-full px-8 pb-[220px] relative z-[2] flex-1">
          
          {/* USER HEADER SECTION */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 p-6 text-white font-sans">
            <div className="w-16 h-16 flex-shrink-0">
              <img 
                src={UserIcon} 
                alt="User" 
                className="w-full h-full rounded-full object-cover shadow-sm" 
              />
            </div>
            
            <div className="flex flex-col gap-1">
              <h2 className="text-2xl font-bold tracking-tight">
                {profile.name || "User"}
              </h2>
              
              <div className="flex items-center">
                <div className="flex items-center gap-1.5 bg-[#208344] px-3 py-1 rounded-full">
                  <img src={DiamondIcon} alt="Diamond" className="w-3.5 h-3.5" />
                  <span className="text-sm font-semibold text-white">Legacy</span>
                </div>
              </div>
            </div>
            
            <p className="w-full mt-2 text-gray-300 text-lg opacity-90">
              Welcome back! Your premium experience awaits.
            </p>
          </div>

          <RecommendedForYou />
          
          {!isLoadingOfficer && relationshipOfficer && (
            <div className="bg-white/5 rounded-2xl py-2 px-4 flex justify-between items-center mb-8">
              <div className="space-y-0">
                <p className="text-[10px] text-slate-400 uppercase">Your relationship Officer</p>
                <p className="font-semibold leading-tight text-white">{relationshipOfficer?.agent_name || "N/A"}</p>
              </div>
              <div className="flex gap-3 text-slate-300">
                {relationshipOfficer?.phone && <a href={`tel:${relationshipOfficer.phone}`}><Phone size={18} /></a>}
                {relationshipOfficer?.email && <a href={`mailto:${relationshipOfficer.email}`}><Mail size={18} /></a>}
              </div>
            </div>
          )}
     
           
            <div style={{ position: "relative", padding: "0 12px" }}>
            <ScrollButton
              onClick={() => scrollPolicyCards("left")}
              disabled={!canScrollLeft}
              direction="left"
            >
              <ChevronLeft size={20} />
            </ScrollButton>
            <ScrollButton
              onClick={() => scrollPolicyCards("right")}
              disabled={!canScrollRight}
              direction="right"
            >
              <ChevronRight size={20} />
            </ScrollButton>
             <Box
              ref={policyScrollRef}
              sx={{
                display: "flex",
                gap: 2,
                overflowX: "auto",
                py: 2,
                scrollSnapType: "x mandatory",
                "&::-webkit-scrollbar": { display: "none" },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
                "& > *": {
                  flexShrink: 0,
                },
              }}
            >
              {[...getBasePolicyCardData(pensionSummaryData, policySummaryData, dasSummaryData), ...corporateCards, ...solutionsCard]
                .filter((card) => !card.hidden)
                .map((data, idx) => {
                const isSolutions = data.label === 'SOLUTIONS FOR YOU';
                const card = (
                  <PolicyCard
                    key={idx}
                    data={data}
                    idx={idx}
                    onClick={() => {
                      const isCorporate = data.isCorporate || data.label === 'Corporate Account';
                      if (isCorporate) {
                        if (data.isLinkCard || !data.corpRaw || data.corpRaw.length === 0) {
                          process.env.REACT_APP_COMING_SOON_BUTTONS === 'true' 
                            ? notifyUser('info', 'Coming Soon!', 'Info') 
                            : setShowLinkModal(true);
                        } else {
                          navigate('/dashboard/corporate');
                        }
                      } else if (isSolutions) {
                        notifyUser('info', 'Coming soon!', 'Info');
                      } else {
                        const routeMap = {
                          'life': '/dashboard/life',
                          'pension': '/dashboard/pension',
                          'das': '/dashboard/das'
                        };
                        const route = routeMap[data.icon] || '/dashboard';
                        navigate(route);
                      }
                    }}
                  />
                );
                return isSolutions ? <div key={idx} className="hidden">{card}</div> : card;
              })}
            </Box>
          </div>
        </div>
      </PatternBackground>
       
      <div className="bg-white relative z-[1000]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-12 pb-4 -mt-[220px]">
           <div className="flex justify-between items-center mb-4">
            <h2 className="text-[#fff] mb-0 font-semibold">Quick Access</h2>
          </div>
          <div className="flex flex-row justify-start gap-[16px] mb-6 w-full overflow-x-auto no-scrollbar pb-4">
            <div className="flex flex-row justify-start gap-[16px] flex-nowrap">
              <div className="hidden"><QuickAction icon={<TrendingUp color="#10b981"/>} label="Financial Insights" onClick={() => notifyUser('info', 'Coming soon!', 'Info')} /></div>
              <div className="hidden"><QuickAction icon={<Gift color="#10b981"/>} label="Complimentary Services" onClick={() => notifyUser('info', 'Coming soon!', 'Info')} /></div>
              <QuickAction icon={<CreditCard color="#10b981"/>} label="Legacy Card" onClick={() => onNavigate('card')} />
              <QuickAction icon={<BarChart3 color="#10b981"/>} label="Financial Analysis" onClick={() => setShowFinancialWellness(true)} />
              <div className="hidden"><QuickAction icon={<ClipboardList color="#10b981"/>} label="Track Claims" onClick={() => notifyUser('info', 'Coming soon!', 'Info')} /></div>
            </div>
          </div>
          
          <div className="hidden">
            <DashboardNews />
          </div>
        </div>
      </div>
       
      <DashboardFooter />

      <LinkAccountModal visible={showLinkModal} onClose={() => setShowLinkModal(false)} 
        onSuccess={() => { setShowLinkModal(false); setShowSuccessModal(true); queryClient.invalidateQueries(['corporate-accounts']); }} 
      />
      <LinkSuccessModal visible={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
      <DashboardModal
        open={showDashboardModal}
        onClose={() => setShowDashboardModal(false)}
      />
      <FinancialWellnessModal
        open={showFinancialWellness}
        onClose={() => setShowFinancialWellness(false)}
      />
    </div>
  );
}

export default AffluentDashboardPage;
